// src/pages/TopDomains.jsx 
import React, { useEffect, useState, useCallback, useContext, useRef } from "react";
import { Card, CardBody, Spinner, Button } from "reactstrap";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  CartesianGrid,
} from "recharts";
import { request } from "../../utils/request";
import { ColorModeContext } from "../../index";
import { motion } from "framer-motion";
import Modal from "react-modal";
import { CopyToClipboard } from "react-copy-to-clipboard";
import "./dashboard.scss";

/* ---------- Styles ---------- */
const injectMiniWidgetStyles = () => {
  if (document.getElementById("mini-widget-polish")) return;
  const css = `
    .mw-light-root { --text: #0b1220; --muted: #666; --tile-border: rgba(6,15,34,0.04); --shadow: 0 1px 2px rgba(0,0,0,0.06); }
    .mw-dark-root  { --text: #f5f5f5; --muted: #c9c9c9; --tile-border: rgba(255,255,255,0.05); --shadow: 0 1px 3px rgba(0,0,0,0.35); }

    .polish-card { border-radius: 12px; overflow: visible; position: relative; }
    .polish-card .polish-body { padding: 0.85rem; box-sizing: border-box; }
    .polish-header { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:8px; }
    .polish-title { margin:0; font-size:1.0rem; font-weight:700; color:var(--text); letter-spacing:0.2px; }
    .polish-sub { margin:0; font-size:0.85rem; color:var(--muted); }
    .polish-chart-wrap { display:flex; align-items:center; justify-content:center; width:100%; }

    .copied-toast {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 90;
      padding: 8px 12px;
      border-radius: 8px;
      font-weight:700;
      box-shadow: 0 8px 26px rgba(3,7,18,0.12);
    }

    @media (max-width:720px) {
      .polish-header { flex-direction: column; align-items: flex-start; gap:8px; }
    }
  `;
  const s = document.createElement("style");
  s.id = "mini-widget-polish";
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);
};

/* ---------- Utilities ---------- */
function normalizeArrayResp(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (resp.data) {
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp.data.docs)) return resp.data.docs;
  }
  if (Array.isArray(resp.docs)) return resp.docs;
  const firstArray = Object.values(resp).find((v) => Array.isArray(v));
  return Array.isArray(firstArray) ? firstArray : [];
}

const extractDomain = (log) => {
  try {
    if (!log) return null;
    const ed = log.eventData || {};
    if (ed.history && typeof ed.history === "object") {
      if (ed.history.domain) return String(ed.history.domain).toLowerCase();
      if (ed.history.url) {
        try { return new URL(ed.history.url).hostname.toLowerCase(); } catch {
          const m = String(ed.history.url).match(/https?:\/\/([^\/]+)/i);
          if (m && m[1]) return m[1].toLowerCase();
        }
      }
    }
    if (ed.domain) return String(ed.domain).toLowerCase();
    if (log.domain) return String(log.domain).toLowerCase();
    if (ed.data && ed.data.domain) return String(ed.data.domain).toLowerCase();
    if (ed.payload && ed.payload.domain) return String(ed.payload.domain).toLowerCase();
  } catch (e) {}
  return null;
};

const isIpAddress = (value) => {
  if (!value) return false;
  const v = String(value).trim();

  // IPv4
  if (/^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/.test(v)) {
    return true;
  }

  // IPv6 (simple practical check)
  if (v.includes(":") && /^[0-9a-f:]+$/i.test(v)) {
    return true;
  }

  return false;
};

const shortName = (domain) => {
  if (!domain) return "";
  let d = String(domain).trim().toLowerCase().replace(/:\d+$/, "").replace(/^www\./, "");

  // If it's an IP, keep the full IP exactly as-is
  if (isIpAddress(d)) return d;

  d = d.replace(/(\.co\.uk|\.com|\.org|\.net|\.io|\.co|\.in|\.dev|\.app|\.gov|\.edu|\.info|\.biz)$/i, "");
  const parts = d.split(".");
  return parts[0] || d;
};

/* Truncate a label string to maxLen chars, appending … */
const truncateLabel = (str, maxLen = 12) => {
  if (!str) return "";
  return str.length > maxLen ? str.slice(0, maxLen - 1) + "…" : str;
};

function lightenHex(hex, percent) {
  try {
    const c = hex.replace("#", "");
    const num = parseInt(c, 16);
    const r = Math.min(255, (num >> 16) + Math.round((255 - (num >> 16)) * (percent / 100)));
    const g = Math.min(255, ((num >> 8) & 0x00ff) + Math.round((255 - ((num >> 8) & 0x00ff)) * (percent / 100)));
    const b = Math.min(255, (num & 0x0000ff) + Math.round((255 - (num & 0x0000ff)) * (percent / 100)));
    const out = (r << 16) | (g << 8) | b;
    return `#${out.toString(16).padStart(6, "0")}`;
  } catch {
    return hex;
  }
}

const PALETTE = [
  "#0b1d51", "#F2914a", "#27ae60",
  "#2d9cdb", "#E84855", "#ff9f1c", "#6f42c1",
  "#00bcd4", "#9c27b0", "#795548",
];

Modal.setAppElement("#root");

/* ---------- Custom YAxis Tick ---------- */
/* Renders a truncated label so no bar-row overflows */
const CustomYAxisTick = ({ x, y, payload, isDark, maxChars = 11 }) => {
  const rawLabel = String(payload.value || "");
  const label = isIpAddress(rawLabel) ? rawLabel : truncateLabel(rawLabel, maxChars);

  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fontSize={12}
      fontWeight={700}
      fill={isDark ? "#e2e8f0" : "#1e293b"}
    >
      {label}
    </text>
  );
};

/* ---------- Custom Bar Label (count, placed smartly) ---------- */
/* Renders inside the bar if bar is wide enough, otherwise just after it */
const CustomBarLabel = (props) => {
  const { x, y, width, height, value, isDark, barMaxValue } = props;
  if (!value) return null;

  const text = String(value);
  /* Approximate pixel width of the label text at ~11px */
  const textPx = text.length * 7 + 8;
  /* Put label INSIDE bar when bar is wide enough; outside otherwise */
  const insideThreshold = textPx + 12;

  if (width >= insideThreshold) {
    return (
      <text
        x={x + width - 8}
        y={y + height / 2}
        dy={4}
        textAnchor="end"
        fontSize={11}
        fontWeight={800}
        fill="#fff"
      >
        {text}
      </text>
    );
  }

  return (
    <text
      x={x + width + 5}
      y={y + height / 2}
      dy={4}
      textAnchor="start"
      fontSize={11}
      fontWeight={700}
      fill={isDark ? "#e2e8f0" : "#1e293b"}
    >
      {text}
    </text>
  );
};

/* ---------- Custom Tooltip ---------- */
const CustomTooltip = ({ active, payload, isDark }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div
      style={{
        background: isDark ? "#0f172a" : "#fff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.10)"}`,
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: 13,
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        maxWidth: 220,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 3, color: isDark ? "#f1f5f9" : "#0f172a", wordBreak: "break-all" }}>
        {d.domain}
      </div>
      <div style={{ color: isDark ? "#94a3b8" : "#64748b" }}>
        <span style={{ fontWeight: 700, color: isDark ? "#fff" : "#0b1d51" }}>{d.count}</span> visits
        {d.uniqueUsers > 0 && (
          <span> · {d.uniqueUsers} user{d.uniqueUsers !== 1 ? "s" : ""}</span>
        )}
      </div>
    </div>
  );
};

/* ---------- Component ---------- */
export default function TopAccessedDomains() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  useEffect(() => injectMiniWidgetStyles(), []);

  const [loading, setLoading] = useState(false);
  const [topDomains, setTopDomains] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const [totalUniqueDomains, setTotalUniqueDomains] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(-1);

  const copyTimer = useRef(null);
  const fetchedOnce = useRef(false);

  const fetchTopDomains = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await request(`logs?page=1&limit=20000&eventType=history`, { method: "GET" }).catch(async () => {
        return await request(`logs?page=1&limit=20000`, { method: "GET" });
      });

      const logs = normalizeArrayResp(resp);
      const map = new Map();

      for (const lg of logs) {
        if (!lg) continue;
        const type = (lg.eventType || lg.type || "").toString().toLowerCase();
        if (type && type !== "history" && type !== "historyvisit" && type !== "webhistory") continue;

        const domain = extractDomain(lg);
        if (!domain) continue;
        const key = domain;

        if (!map.has(key)) {
          map.set(key, { domain: key, count: 0, firstSeen: null, lastSeen: null, sampleUrls: new Set(), users: new Set() });
        }
        const e = map.get(key);
        e.count += 1;

        const ts = lg.timestamp || lg.createdAt || (lg.eventData && lg.eventData.data && lg.eventData.data.timestamp) || null;
        const dt = ts ? new Date(ts) : null;
        if (dt && !isNaN(dt.getTime())) {
          if (!e.firstSeen || dt < e.firstSeen) e.firstSeen = dt;
          if (!e.lastSeen || dt > e.lastSeen) e.lastSeen = dt;
        }

        const url = lg?.eventData?.history?.url || lg?.eventData?.data?.url || lg?.eventData?.payload?.url || null;
        if (url && e.sampleUrls.size < 6) e.sampleUrls.add(url);

        const uid = lg.userId || lg.user || lg.user_id || null;
        if (uid) e.users.add(String(uid));
      }

      setTotalUniqueDomains(map.size);

      const arr = Array.from(map.values()).map((e) => ({
        domain: e.domain,
        short: shortName(e.domain),
        count: e.count,
        firstSeen: e.firstSeen,
        lastSeen: e.lastSeen,
        sampleUrls: Array.from(e.sampleUrls).slice(0, 5),
        uniqueUsers: e.users.size,
      }));

      arr.sort((a, b) => b.count - a.count);
      setTopDomains(arr.slice(0, 5));
      setFetchedAt(new Date());
    } catch (err) {
      console.error("TopAccessedDomains fetch error:", err);
      setTopDomains([]);
      setTotalUniqueDomains(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (fetchedOnce.current) return;
    fetchedOnce.current = true;
    fetchTopDomains();
    return () => { if (copyTimer.current) clearTimeout(copyTimer.current); };
  }, [fetchTopDomains]);

  const openDetails = (d) => { setSelected(d); setCopied(false); };
  const closeModal = () => setSelected(null);

  const handleCopy = () => {
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1700);
  };

  const totalVisits = topDomains.reduce((s, t) => s + (t.count || 0), 0);

  const chartData = topDomains.map((t, idx) => ({
    ...t,
    /* Use full IP, otherwise truncated short name for YAxis key */
    label: isIpAddress(shortName(t.domain)) ? shortName(t.domain) : truncateLabel(shortName(t.domain), 11),
    color: PALETTE[idx % PALETTE.length],
    gradient: lightenHex(PALETTE[idx % PALETTE.length], 22),
  }));

  /* Max value used for smart label inside/outside decision */
  const barMaxValue = chartData.length ? Math.max(...chartData.map((c) => c.count)) : 1;

  /* Fixed narrow YAxis width — labels are always ≤ 11 chars × ~8px ≈ 88px, add a few px buffer */
  const Y_AXIS_WIDTH = 96;
  /* Right margin to give outside labels space without overflowing */
  const RIGHT_MARGIN = 38;

  const rootClass = isDark ? "mw-dark-root" : "mw-light-root";

  return (
    <div className={`widget-card ${rootClass}`} style={{ width: "100%" }}>
      <Card
        className="polish-card"
        style={{
          height: "100%",
          backgroundColor: isDark ? "#252b3b" : "#fff",
          border: isDark ? "none" : "1px solid #e0e0e0",
          borderRadius: "12px",
          position: "relative",
        }}
      >
        {copied && (
          <div
            className="copied-toast"
            style={{
              backgroundColor: isDark ? "#0b1220" : "#fff",
              color: isDark ? "#fff" : "#0b1d51",
            }}
          >
            Copied ✓
          </div>
        )}

        <CardBody className={isDark ? "text-white" : ""} style={{ padding: "0.85rem" }}>
          {/* ── Header ── */}
          <div className="polish-header" style={{ padding: "0 0 8px 0" }}>
            <div>
              <h5 className="polish-title" style={{ margin: 0 }}>Top Accessed Domains / Apps</h5>
              <div className="polish-sub" style={{ marginTop: 4 }}>Top 5 visited domains</div>
            </div>

            {/* ── Two stat pills, right-aligned ── */}
            <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexShrink: 0 }}>
              {/* Stat 1 — total visits */}
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                padding: "5px 12px",
                borderRadius: 8,
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(11,29,81,0.05)",
                minWidth: 70,
              }}>
                <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1, color: isDark ? "#fff" : "#0b1d51" }}>
                  {totalVisits.toLocaleString()}
                </span>
                <span style={{ fontSize: 10, marginTop: 3, color: isDark ? "#9aa4b2" : "#6b7280", whiteSpace: "nowrap" }}>
                  Total visits
                </span>
              </div>

              {/* Stat 2 — unique domains */}
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                padding: "5px 12px",
                borderRadius: 8,
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(11,29,81,0.05)",
                minWidth: 70,
              }}>
                <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1, color: isDark ? "#fff" : "#0b1d51" }}>
                  {totalUniqueDomains.toLocaleString()}
                </span>
                <span style={{ fontSize: 10, marginTop: 3, color: isDark ? "#9aa4b2" : "#6b7280", whiteSpace: "nowrap" }}>
                  Unique domains
                </span>
              </div>
            </div>
          </div>

          {/* ── Chart ── */}
          <motion.div whileHover={{ y: -2 }} style={{ padding: "0 0 4px 0" }}>
            <div className="polish-chart-wrap" style={{ minHeight: 170, width: "100%" }}>
              {loading ? (
                <div className="d-flex align-items-center justify-content-center" style={{ width: "100%", height: 170 }}>
                  <Spinner color={isDark ? "light" : "primary"} />
                </div>
              ) : chartData.length === 0 ? (
                <div className="d-flex align-items-center justify-content-center" style={{ width: "100%", height: 170 }}>
                  <div className={isDark ? "text-muted" : "text-secondary"}>No domain history found</div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={170}>
                  <BarChart
                    layout="vertical"
                    data={chartData}
                    /*
                     * top / bottom: normal breathing room
                     * left:  0  — YAxis handles its own width via `width` prop
                     * right: RIGHT_MARGIN — space for outside count labels
                     */
                    margin={{ top: 4, right: RIGHT_MARGIN, left: 0, bottom: 4 }}
                    onMouseLeave={() => setHoverIndex(-1)}
                  >
                    <defs>
                      {chartData.map((c, i) => (
                        <linearGradient id={`grad-td-${i}`} key={`g-${i}`} x1="0" x2="1">
                          <stop offset="0%" stopColor={c.color} stopOpacity="1" />
                          <stop offset="100%" stopColor={c.gradient} stopOpacity="1" />
                        </linearGradient>
                      ))}
                    </defs>

                    <CartesianGrid
                      stroke={isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}
                      vertical={false}
                    />

                    <XAxis
                      type="number"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 10, fill: isDark ? "#94a3b8" : "#94a3b8" }}
                      /* Hide X axis ticks — counts are rendered inline on bars */
                      hide
                    />

                    <YAxis
                      dataKey="label"
                      type="category"
                      width={Y_AXIS_WIDTH}
                      tickLine={false}
                      axisLine={false}
                      interval={0}
                      tick={(props) => (
                        <CustomYAxisTick {...props} isDark={isDark} maxChars={11} />
                      )}
                    />

                    <Tooltip
                      content={<CustomTooltip isDark={isDark} />}
                      cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" }}
                    />

                    <Bar
                      dataKey="count"
                      barSize={20}
                      radius={[0, 4, 4, 0]}
                      onMouseEnter={(_, idx) => setHoverIndex(idx)}
                      onClick={(data, idx) => openDetails(chartData[idx])}
                      style={{ cursor: "pointer" }}
                      label={(props) => (
                        <CustomBarLabel
                          {...props}
                          isDark={isDark}
                          barMaxValue={barMaxValue}
                        />
                      )}
                    >
                      {chartData.map((entry, index) => {
                        const opacity = hoverIndex === -1 ? 1 : hoverIndex === index ? 1 : 0.4;
                        return (
                          <Cell
                            key={`cell-${index}`}
                            fill={`url(#grad-td-${index})`}
                            fillOpacity={opacity}
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* ── Footer ── */}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
              <div style={{ fontSize: "0.75rem", color: isDark ? "#9aa4b2" : "#6b7280" }}>
                Showing top {chartData.length} domains
              </div>
              <div style={{ fontSize: "0.75rem", color: isDark ? "#9aa4b2" : "#6b7280" }}>
                {fetchedAt ? `Updated ${fetchedAt.toLocaleTimeString()}` : ""}
              </div>
            </div>
          </motion.div>
        </CardBody>
      </Card>

      {/* ── Detail Modal ── */}
      <Modal
        isOpen={!!selected}
        onRequestClose={closeModal}
        contentLabel="Domain details"
        overlayClassName="ReactModal__Overlay"
        className="td-modal"
        style={{
          overlay: {
            backgroundColor: "rgba(2,6,23,0.55)",
            zIndex: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        {selected && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <div style={{ minWidth: 0 }}>
                <h4 style={{ margin: 0, wordBreak: "break-all" }}>{selected.domain}</h4>
                <div style={{ color: isDark ? "#9aa4b2" : "#6b7280", marginTop: 6 }}>
                  <strong>{selected.count}</strong> visits &nbsp;·&nbsp; {selected.uniqueUsers ?? 0} unique user{selected.uniqueUsers !== 1 ? "s" : ""}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                <CopyToClipboard text={selected.domain} onCopy={handleCopy}>
                  <Button color="primary" size="sm">Copy</Button>
                </CopyToClipboard>
                <Button color="secondary" size="sm" onClick={closeModal}>Close</Button>
              </div>
            </div>

            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>First seen</div>
                <div style={{ color: isDark ? "#9aa4b2" : "#6b7280", fontSize: 13 }}>
                  {selected.firstSeen ? new Date(selected.firstSeen).toLocaleString() : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Last seen</div>
                <div style={{ color: isDark ? "#9aa4b2" : "#6b7280", fontSize: 13 }}>
                  {selected.lastSeen ? new Date(selected.lastSeen).toLocaleString() : "—"}
                </div>
              </div>
            </div>

            {selected.sampleUrls && selected.sampleUrls.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Sample URLs</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {selected.sampleUrls.map((s, i) => (
                    <a
                      key={i}
                      href={s}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: isDark ? "#9ec5ff" : "#0b3a8a",
                        textDecoration: "underline",
                        fontSize: 13,
                        wordBreak: "break-all",
                      }}
                    >
                      {s}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}